import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessType } from '@/contexts/BusinessTypeContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Package, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Search, Download, Upload, Plus, Minus, Edit, BarChart3, 
  ArrowUpDown, FileSpreadsheet, Bell, Loader2, RefreshCw
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils-helpers';

export default function VendorInventoryDashboard() {
  const { config } = useBusinessType();
  const navigate = useNavigate();
  
  const [inventory, setInventory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustQuantity, setAdjustQuantity] = useState(0);
  const [adjustReason, setAdjustReason] = useState('restock');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [lowStockAlerts, setLowStockAlerts] = useState(null);
  const [showAlertsDetail, setShowAlertsDetail] = useState(false);
  
  const fetchLowStockAlerts = async () => {
    try {
      const response = await api.get('/vendor/inventory/low-stock-alerts');
      setLowStockAlerts(response.data);
    } catch (err) {
      console.error('Failed to load low stock alerts:', err);
    }
  };
  
  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stockFilter === 'low') params.low_stock_only = true;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      
      const response = await api.get('/vendor/inventory', { params });
      setInventory(response.data.items || []);
      setSummary(response.data.summary || {});
    } catch (err) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchLowStockAlerts();
  }, [categoryFilter, stockFilter]);

  const handleAdjustStock = async () => {
    if (!selectedItem || adjustQuantity === 0) {
      toast.error('Please enter a valid adjustment quantity');
      return;
    }

    setAdjusting(true);
    try {
      await api.post(`/vendor/inventory/${selectedItem.id}/adjust`, {
        adjustment: parseInt(adjustQuantity),
        reason: adjustReason,
        notes: adjustNotes
      });
      
      toast.success(`Stock adjusted for ${selectedItem.name}`);
      setSelectedItem(null);
      setAdjustQuantity(0);
      setAdjustNotes('');
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setAdjusting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/vendor/inventory/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Inventory exported successfully');
    } catch (err) {
      toast.error('Failed to export inventory');
    }
  };

  const filteredInventory = inventory.filter(item => {
    if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const categories = [...new Set(inventory.map(item => item.category))].filter(Boolean);

  const getStockStatus = (item) => {
    const stock = item.stock_quantity || 0;
    const threshold = item.low_stock_threshold || 10;
    
    if (stock === 0) return { label: 'Out of Stock', color: 'destructive', icon: AlertTriangle };
    if (stock <= threshold) return { label: 'Low Stock', color: 'warning', icon: TrendingDown };
    if (stock > threshold * 3) return { label: 'Well Stocked', color: 'success', icon: CheckCircle };
    return { label: 'Normal', color: 'default', icon: Package };
  };

  const getStockPercentage = (item) => {
    const stock = item.stock_quantity || 0;
    const threshold = item.low_stock_threshold || 10;
    const max = threshold * 5; // Consider 5x threshold as 100%
    return Math.min((stock / max) * 100, 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">
            Inventory Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor and manage your {config.itemsShort.toLowerCase()} stock levels
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchInventory}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total {config.itemsLabel}</p>
                  <p className="text-2xl font-bold">{summary.total_items || 0}</p>
                </div>
                <Package className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:bg-gray-50" onClick={() => {
            setStockFilter('low');
            setShowAlertsDetail(true);
          }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock Items</p>
                  <p className="text-2xl font-bold text-amber-600">{summary.low_stock_count || 0}</p>
                </div>
                <TrendingDown className="h-8 w-8 text-amber-600" />
              </div>
              {lowStockAlerts && lowStockAlerts.summary.warning > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                  <Bell className="h-3 w-3" />
                  <span>{lowStockAlerts.summary.warning} need attention</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600">{summary.out_of_stock_count || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              {lowStockAlerts && lowStockAlerts.summary.critical > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Action required!</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Stock Units</p>
                  <p className="text-2xl font-bold text-green-600">{summary.total_stock_units || 0}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${config.itemsShort.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="inventory-search"
              />
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger data-testid="stock-filter">
                <SelectValue placeholder="Stock Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stock Levels</SelectItem>
                <SelectItem value="low">Low Stock Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory Items</CardTitle>
          <CardDescription>
            {filteredInventory.length} {config.itemsShort.toLowerCase()} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No inventory items found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredInventory.map((item) => {
                const status = getStockStatus(item);
                const StatusIcon = status.icon;
                const stockPercentage = getStockPercentage(item);
                
                return (
                  <div
                    key={item.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`inventory-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold">{item.name}</h4>
                          <Badge variant={status.color === 'destructive' ? 'destructive' : status.color === 'warning' ? 'secondary' : 'default'}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                          {item.category && (
                            <Badge variant="outline">{item.category}</Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Current Stock</p>
                            <p className="font-semibold text-lg">{item.stock_quantity || 0} {item.unit || 'units'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Low Stock Alert</p>
                            <p className="font-semibold">{item.low_stock_threshold || 10} {item.unit || 'units'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">SKU</p>
                            <p className="font-mono text-xs">{item.sku || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Price</p>
                            <p className="font-semibold">{formatCurrency(item.price || 0)}</p>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Stock Level</span>
                            <span className="text-xs font-medium">{Math.round(stockPercentage)}%</span>
                          </div>
                          <Progress value={stockPercentage} className="h-2" />
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedItem(item)}
                          data-testid={`adjust-stock-${item.id}`}
                        >
                          <ArrowUpDown className="h-4 w-4 mr-1" />
                          Adjust
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/vendor/menu/${item.id}`)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock for {selectedItem?.name}</DialogTitle>
            <DialogDescription>
              Current stock: {selectedItem?.stock_quantity || 0} {selectedItem?.unit || 'units'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Adjustment Quantity</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustQuantity(Math.max(-selectedItem.stock_quantity, adjustQuantity - 10))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={adjustQuantity}
                  onChange={(e) => setAdjustQuantity(parseInt(e.target.value) || 0)}
                  className="text-center"
                  data-testid="adjust-quantity-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustQuantity(adjustQuantity + 10)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                New stock will be: {(selectedItem?.stock_quantity || 0) + adjustQuantity}
              </p>
            </div>

            <div>
              <Label>Reason</Label>
              <Select value={adjustReason} onValueChange={setAdjustReason}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restock">Restock / Delivery</SelectItem>
                  <SelectItem value="sale">Sale / Order Fulfillment</SelectItem>
                  <SelectItem value="damage">Damage / Spoilage</SelectItem>
                  <SelectItem value="return">Customer Return</SelectItem>
                  <SelectItem value="adjustment">Inventory Count Adjustment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Additional details..."
                value={adjustNotes}
                onChange={(e) => setAdjustNotes(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedItem(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleAdjustStock}
              disabled={adjusting || adjustQuantity === 0}
              data-testid="confirm-adjust-stock"
            >
              {adjusting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
